'use client';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Close from '@mui/icons-material/Close';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { scrollToViewPromise } from '../../../TableOfContents/scrollToViewPromise';
import { MOBILE_TOC_GUTTER } from './constants';
import type { TableOfContentsEntry } from './types';
import { useMobileTocBar } from './useMobileTocBar';

const SCROLL_OFFSET = 60;
const BAR_Z_INDEX = 2000;
const SHEET_Z_INDEX = BAR_Z_INDEX + 100;
const SAFE_AREA_TOP = 'env(safe-area-inset-top, 0px)';

function readSafeAreaTop(): number {
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;padding-top:${SAFE_AREA_TOP};visibility:hidden;pointer-events:none`;
  document.documentElement.appendChild(probe);
  const inset = probe.offsetHeight;
  probe.remove();
  return inset;
}

type MobileTocBarProps = {
  items: TableOfContentsEntry[];
};

export function MobileTocBar({ items }: MobileTocBarProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(SCROLL_OFFSET);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { isScrolled, isRowVisible, progress, activeTarget } = useMobileTocBar(items, scrollOffset);

  useEffect(() => {
    const updateOffset = () => setScrollOffset(SCROLL_OFFSET + readSafeAreaTop());
    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setIsSheetOpen(false);
    }
  }, [isDesktop]);

  const closeSheet = () => setIsSheetOpen(false);

  const handleItemClick = (event: MouseEvent<HTMLAnchorElement>, target: string) => {
    event.preventDefault();
    closeSheet();
    scrollToViewPromise(target, 'smooth', scrollOffset, true);
  };

  return (
    <>
      <Box
        aria-hidden={!isScrolled || undefined}
        inert={!isScrolled}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: BAR_Z_INDEX,
          // The bar is pinned to the screen top. On iPhone the Live Activity /
          // status-bar band sits in that same strip, and the white background
          // has to include it or the page shows through behind the labels.
          paddingTop: SAFE_AREA_TOP,
          backgroundColor: 'common.white',
          borderBottom: '1px solid',
          borderColor: 'neutral.lighter',
          visibility: isScrolled ? 'visible' : 'hidden',
          transform: isScrolled ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.2s ease',
        }}
      >
        <Box
          aria-hidden={!isRowVisible || undefined}
          inert={!isRowVisible}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: MOBILE_TOC_GUTTER,
            height: isRowVisible ? 44 : 0,
            minHeight: 0,
            overflow: 'hidden',
            opacity: isRowVisible ? 1 : 0,
            visibility: isRowVisible ? 'visible' : 'hidden',
            transition: 'height 0.2s ease',
          }}
        >
          <ButtonBase
            onClick={() => setIsSheetOpen(true)}
            sx={{ gap: 1, color: 'primary.main', fontWeight: 700, fontSize: 14 }}
          >
            <FormatListBulleted fontSize="small" />
            On this page
          </ButtonBase>
          <ButtonBase
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{ gap: 0.5, color: 'primary.main', fontWeight: 700, fontSize: 14 }}
          >
            Back to top
            <ArrowUpward fontSize="small" />
          </ButtonBase>
        </Box>
        <Box sx={{ height: 3, width: '100%', backgroundColor: 'neutral.lighter2' }}>
          <Box
            sx={{ height: '100%', width: `${progress}%`, backgroundColor: 'nwGreens.logoLight' }}
          />
        </Box>
      </Box>
      <Drawer
        anchor="bottom"
        open={isSheetOpen && !isDesktop}
        onClose={closeSheet}
        sx={{ zIndex: SHEET_Z_INDEX }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              margin: 0,
              width: '100%',
              maxHeight: '40vh',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            px: MOBILE_TOC_GUTTER,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'neutral.lighter',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
            <FormatListBulleted fontSize="small" />
            <Typography component="span" variant="bodySmallBold" sx={{ color: 'text.primary' }}>
              On this page
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={closeSheet}
            sx={{
              width: 28,
              height: 28,
              p: 0,
              boxSizing: 'border-box',
              border: '1px solid',
              borderColor: 'neutral.lighter',
              borderRadius: '50%',
              fontSize: 16,
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Box
          component="ol"
          sx={{
            listStyle: 'none',
            m: 0,
            px: MOBILE_TOC_GUTTER,
            pb: 2,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {items.map(item => {
            const isActive = item.target === activeTarget;

            return (
              <Box
                component="li"
                key={item.target}
                sx={{ display: 'flex', alignItems: 'center', minHeight: 44 }}
              >
                <Link
                  href={`#${item.target}`}
                  onClick={event => handleItemClick(event, item.target)}
                  underline="none"
                  variant={isActive ? 'bodySmallBold' : 'bodySmall'}
                  aria-current={isActive ? 'location' : undefined}
                  sx={{ color: isActive ? 'primary.main' : 'text.primary' }}
                >
                  {item.textContent}
                </Link>
              </Box>
            );
          })}
        </Box>
      </Drawer>
    </>
  );
}
